import React, { useEffect, useState, useRef } from 'react';
import '../style/components/MarketWatchlist.scss';

// dex: null = standard perps, dex: 'xyz' = HIP-3 tradfi perps
// ytdTicker / changeTicker: Yahoo Finance tickers (xyz perps don't support candleSnapshot)
const ASSETS = [
  { id: 'BTC',   label: 'Bitcoin', symbol: 'BTC',       dex: null,  prefix: '$', decimals: 2, ytdTicker: null,  changeTicker: null  },
  { id: 'ES',    label: 'E-mini S&P 500', symbol: 'xyz:SP500', dex: 'xyz', prefix: '$', decimals: 1, ytdTicker: 'SPY', changeTicker: 'SPY' },
  { id: 'GC',    label: 'Gold Futures',   symbol: 'xyz:GOLD',  dex: 'xyz', prefix: '$', decimals: 2, ytdTicker: 'GC=F', changeTicker: 'GC=F' },
  { id: 'CL',    label: 'WTI Oil', symbol: 'xyz:CL',    dex: 'xyz', prefix: '$', decimals: 2, ytdTicker: 'CL=F', changeTicker: 'CL=F' },
];

const HYPE_URL = 'https://api.hyperliquid.xyz/info';
const REFRESH_MS = 15000;
const BACKEND = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'http://localhost:3003'
  : 'https://api.blockhivetechnologies.com';

export default function MarketWatchlist() {
  const [prices, setPrices]     = useState({});
  const [changes, setChanges]   = useState({});
  const [ytd, setYtd]           = useState({});
  const [updated, setUpdated]   = useState(null);
  const [error, setError]       = useState(null);
  const intervalRef             = useRef(null);

  // Jan 1 2026 00:00:00 UTC
  const JAN1 = 1767243600000;

  async function fetchMids(dex) {
    const body = dex ? { type: 'allMids', dex } : { type: 'allMids' };
    const res  = await fetch(HYPE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return await res.json();
  }

  async function fetchChange(coin, dex) {
    const now  = Date.now();
    const day  = now - 24 * 60 * 60 * 1000;
    // Strip dex prefix (e.g. "xyz:SP500" → "SP500") — candleSnapshot uses plain coin name
    const candleCoin = dex ? coin.replace(/^[^:]+:/, '') : coin;
    const req  = { coin: candleCoin, interval: '1d', startTime: day, endTime: now };
    const body = dex
      ? { type: 'candleSnapshot', dex, req }
      : { type: 'candleSnapshot', req };
    const res  = await fetch(HYPE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const candles = await res.json();
    if (!candles || candles.length === 0) return null;
    const open  = parseFloat(candles[0].o);
    const close = parseFloat(candles[candles.length - 1].c);
    return ((close - open) / open) * 100;
  }

  async function fetchYTDFromYahoo(ticker) {
    const res  = await fetch(`${BACKEND}/ytd?ticker=${encodeURIComponent(ticker)}`);
    const data = await res.json();
    return data.ytd ?? null;
  }

  async function fetchYTDChange(asset) {
    if (asset.ytdTicker) return fetchYTDFromYahoo(asset.ytdTicker);
    // BTC: use Hyperliquid candleSnapshot
    const now = Date.now();
    const [jan1Res, nowRes] = await Promise.all([
      fetch(HYPE_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'candleSnapshot', req: { coin: asset.symbol, interval: '1d', startTime: JAN1, endTime: JAN1 + 86400000 } }) }),
      fetch(HYPE_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'candleSnapshot', req: { coin: asset.symbol, interval: '1d', startTime: now - 86400000, endTime: now } }) }),
    ]);
    const [c1, c2] = await Promise.all([jan1Res.json(), nowRes.json()]);
    if (!c1?.length || !c2?.length) return null;
    return ((parseFloat(c2[c2.length - 1].c) - parseFloat(c1[0].o)) / parseFloat(c1[0].o)) * 100;
  }

  async function fetchAll() {
    try {
      // Fetch std mids, xyz metaAndAssetCtxs (includes prevDayPx), and BTC 24h change in parallel
      const [stdMids, xyzCtxsRes, btcChange] = await Promise.all([
        fetchMids(null),
        fetch(HYPE_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type: 'metaAndAssetCtxs', dex: 'xyz' }) }).then(r => r.json()),
        fetchChange('BTC', null),
      ]);

      // Build price map from std mids + xyz mids
      const xyzMeta = xyzCtxsRes[0].universe;
      const xyzCtxs = xyzCtxsRes[1];
      const xyzPrevDay = {};
      xyzMeta.forEach((asset, i) => {
        const mid = xyzCtxs[i].midPx;
        const prev = xyzCtxs[i].prevDayPx;
        if (mid)  stdMids[asset.name] = mid;
        if (prev) xyzPrevDay[asset.name] = parseFloat(prev);
      });

      const newPrices = {};
      for (const a of ASSETS) {
        const raw = stdMids[a.symbol];
        if (raw !== undefined) newPrices[a.id] = parseFloat(raw);
      }
      setPrices(newPrices);

      // 24h change: BTC from candleSnapshot, xyz from prevDayPx
      const newChanges = {};
      newChanges['BTC'] = btcChange;
      for (const a of ASSETS.filter(a => a.dex === 'xyz')) {
        const mid  = newPrices[a.id];
        const prev = xyzPrevDay[a.symbol];
        if (mid && prev) newChanges[a.id] = ((mid - prev) / prev) * 100;
      }
      setChanges(newChanges);

      // YTD
      const ytdResults = await Promise.allSettled(ASSETS.map(a => fetchYTDChange(a)));
      const newYtd = {};
      ASSETS.forEach((a, i) => {
        if (ytdResults[i].status === 'fulfilled') newYtd[a.id] = ytdResults[i].value;
      });
      setYtd(newYtd);
      setUpdated(new Date());
      setError(null);
    } catch (e) {
      console.error('[MarketWatchlist]', e);
      setError('fetch error');
    }
  }

  useEffect(() => {
    fetchAll();
    intervalRef.current = setInterval(fetchAll, REFRESH_MS);
    return () => clearInterval(intervalRef.current);
  }, []);

  function fmt(id, price) {
    if (price === undefined || price === null) return '—';
    const a = ASSETS.find(x => x.id === id);
    return a.prefix + price.toLocaleString(undefined, {
      minimumFractionDigits: a.decimals,
      maximumFractionDigits: a.decimals,
    });
  }

  function chgClass(pct) {
    if (pct === null || pct === undefined) return '';
    return pct >= 0 ? 'positive' : 'negative';
  }

  function chgText(pct) {
    if (pct === null || pct === undefined) return '—';
    return (pct >= 0 ? '+' : '') + pct.toFixed(2) + '%';
  }

  const timeStr = updated
    ? updated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : '—';

  return (
    <div className="market-watchlist">
      <div className="mw-header">
        <span className="mw-title">MARKETS</span>
        <span className="mw-updated">
          {error ? <span className="mw-error">ERR</span> : timeStr}
        </span>
      </div>

      <div className="mw-col-labels">
        <span>ASSET</span>
        <span>PRICE</span>
        <span>24H</span>
        <span>YTD</span>
      </div>

      {ASSETS.map(a => (
        <div className="mw-row" key={a.id}>
          <div className="mw-asset">
            <span className="mw-symbol">{a.id}</span>
            <span className="mw-label">{a.label}</span>
          </div>
          <span className="mw-price">
            {prices[a.id] !== undefined ? fmt(a.id, prices[a.id]) : <span className="mw-loading">···</span>}
          </span>
          <span className={`mw-change ${chgClass(changes[a.id])}`}>
            {chgText(changes[a.id])}
          </span>
          <span className={`mw-change ${chgClass(ytd[a.id])}`}>
            {chgText(ytd[a.id])}
          </span>
        </div>
      ))}

      <div className="mw-footer">
        via Hyperliquid · {REFRESH_MS / 1000}s refresh
      </div>
    </div>
  );
}
